import { Fragment, useEffect, useState } from 'react';
import { Feuille } from './Feuille.jsx';
import { Bouton, MONO, Titre } from './base.jsx';
import { LIBELLE, STATUTS } from '../data/postes.js';
import { lireNote, sauverNote } from '../local/depot.js';
import { Ballon, Carton, Fleche, Crayon } from './icones.jsx';

const TON = { bon: 'var(--acc2)', attention: 'var(--warn)', mauvais: 'var(--bad)' };
const TEINTES = ['var(--acc)', 'var(--acc2)', 'var(--ink3)', 'var(--line)'];

export function FicheJoueur({ ouverte, joueur, surLeTerrain, matchEnCours, matchPrepare, compteurs = {}, onEvenement, onDemarrer, onDeplacer, onRemplacer, onRetirer, onFermer }) {
  const [note, setNote] = useState('');
  const [edite, setEdite] = useState(false);
  const [corrige, setCorrige] = useState(false);

  useEffect(() => {
    if (!joueur) return;
    let vivant = true;
    lireNote(joueur.id).then((n) => { if (vivant) setNote(n?.texte ?? ''); });
    setEdite(false);
    setCorrige(false);
    return () => { vivant = false; };
  }, [joueur?.id]);

  if (!joueur) return null;

  // Les statistiques réellement renseignées, dans l'ordre d'importance. Le nombre de
  // titularisations n'a de sens qu'accompagné du nombre de matchs — seul, « 6 » ne
  // répond à aucune question.
  const relevé = joueur.stats ?? {};
  const chiffres = [
    { libelle: 'MINUTES', valeur: relevé.minutes },
    { libelle: 'BUTS', valeur: relevé.buts, accent: true },
    { libelle: 'PASSES', valeur: relevé.passes },
    { libelle: 'TITULAIRE', valeur: relevé.titularisations, sur: relevé.matchs || null },
  ].filter((c) => typeof c.valeur === 'number' && c.valeur > 0);

  const agir = (type, delta) => {
    onEvenement?.(type, delta);
    if (delta < 0) setCorrige(false);
  };
  const st = STATUTS[joueur.statut] ?? STATUTS.ok;

  const enregistrerNote = async () => {
    await sauverNote(joueur.id, note.trim());
    setEdite(false);
  };

  return (
    <Feuille ouverte={ouverte} onFermer={onFermer} titre={joueur.nom} sousTitre={`${LIBELLE[joueur.poste] ?? joueur.poste}${joueur.age ? ` · ${joueur.age} ans` : ''}`}>
      <div style={{ padding: '0 18px 22px' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 56, height: 56, flex: 'none', borderRadius: 16, background: 'var(--acc)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: MONO, fontWeight: 700, fontSize: 22, color: 'var(--tokink)',
          }}>{joueur.numero ?? '·'}</div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              {joueur.nat && (
                <span style={{ padding: '2px 6px', borderRadius: 5, background: 'var(--acc)', color: 'var(--tokink)', fontFamily: MONO, fontWeight: 700, fontSize: 9.5, letterSpacing: '.5px' }}>{joueur.nat}</span>
              )}
              {joueur.natNom && <span style={{ fontSize: 12, fontWeight: 600 }}>{joueur.natNom}</span>}
              {joueur.capitaine && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--acc2)' }}>Capitaine</span>}
            </div>
            {joueur.alt?.length > 0 && (
              <div style={{ fontSize: 11, fontFamily: MONO, color: 'var(--ink2)', marginTop: 5 }}>
                Joue aussi : {joueur.alt.map((a) => LIBELLE[a] ?? a).join(', ')}
              </div>
            )}
          </div>

          <span style={{
            padding: '5px 9px', borderRadius: 8, flex: 'none',
            border: `1px solid ${TON[st.ton]}`, color: TON[st.ton],
            fontSize: 10.5, fontWeight: 700,
          }}>{st.libelle}</span>
        </div>

        {joueur.raison && (
          <div style={{ marginBottom: 14, padding: '9px 12px', borderRadius: 11, background: 'var(--surf2)', fontSize: 12, color: 'var(--ink2)' }}>{joueur.raison}</div>
        )}

        {/* ON N'AFFICHE QUE CE QU'ON SAIT.
            Quatre cases à zéro ne disent pas « il n'a pas marqué », elles disent « on
            n'a pas la donnée » — et elles le disent mal, en occupant la place d'une
            information. Une case vide est pire qu'une case absente : elle se lit, et
            elle ment. Aucune statistique connue, aucune rangée. */}
        {chiffres.length > 0 && (
          <div style={{ display: 'flex', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
            {chiffres.map((c, i) => (
              <Fragment key={c.libelle}>
                {i > 0 && <div style={{ width: 1, background: 'var(--line)' }} />}
                <Stat valeur={c.valeur} sur={c.sur} libelle={c.libelle} accent={c.accent} />
              </Fragment>
            ))}
          </div>
        )}

        {/* SUR COMBIEN DE MATCHS. Sans cette ligne, « 90 minutes » se lit comme un
            total de saison alors que c'est un seul match suivi — le chiffre est juste
            et le sens est faux. Ces statistiques viennent des matchs que TU as
            enregistrés, aucune source gratuite ne donnant celles de la saison. */}
        {chiffres.length > 0 && (
          <div style={{ fontSize: 10, fontFamily: MONO, color: 'var(--ink3)', margin: '7px 0 16px' }}>
            sur {relevé.matchs ?? 0} match{(relevé.matchs ?? 0) > 1 ? 's' : ''} que tu as suivi{(relevé.matchs ?? 0) > 1 ? 's' : ''}
          </div>
        )}

        {joueur.repartition?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Titre>POSTES RÉELLEMENT OCCUPÉS</Titre>
            <div style={{ display: 'flex', height: 9, borderRadius: 5, overflow: 'hidden', marginTop: 9, gap: 2 }}>
              {joueur.repartition.slice(0, 4).map((r, i) => (
                <div key={r.code} style={{ width: `${r.part * 100}%`, background: TEINTES[i] }} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 9, flexWrap: 'wrap' }}>
              {joueur.repartition.slice(0, 4).map((r, i) => (
                <span key={r.code} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 2, background: TEINTES[i] }} />
                  {/* Le nombre de fois, pas seulement le pourcentage : « AIG 100 % »
                      sur une seule titularisation n'est pas une tendance. */}
                  <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: 9.5 }}>
                    {r.code} {r.n}<span style={{ color: 'var(--ink3)' }}>×</span>
                  </span>
                </span>
              ))}
            </div>
            <div style={{ fontSize: 10, fontFamily: MONO, color: 'var(--ink3)', marginTop: 7 }}>
              d&apos;après {relevé.titularisations ?? 0} titularisation{(relevé.titularisations ?? 0) > 1 ? 's' : ''}
            </div>
          </div>
        )}

        {/* LA NOTE, repliée par défaut.
            Elle avait un titre, une date, un bouton et trois lignes de texte pour un
            champ qu'on remplit une fois sur dix. Repliée, elle tient sur une ligne et
            garde sa place dans la page ; dépliée, elle est exactement ce qu'elle était.
            Un filet vertical à l'accent la distingue du reste sans titre en capitales. */}
        <div style={{ marginBottom: 16 }}>
          {edite ? (
            <>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Ce que tu as vu de lui…"
                style={{ width: '100%', borderRadius: 12, padding: 11, fontSize: 16, lineHeight: 1.45, background: 'var(--surf2)', border: '1px solid var(--line)', outline: 'none', resize: 'none' }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Bouton ton="valide" large taille={44} onClick={enregistrerNote}>Enregistrer</Bouton>
                <Bouton ton="fantome" taille={44} onClick={() => setEdite(false)}>Annuler</Bouton>
              </div>
            </>
          ) : note ? (
            <button
              onClick={() => setEdite(true)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', textAlign: 'left',
                padding: '9px 11px 9px 12px', borderRadius: 10,
                background: 'var(--surf2)', borderLeft: '2px solid var(--acc)',
              }}
            >
              <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.45, color: 'var(--ink)' }}>{note}</span>
              <span style={{ flex: 'none', color: 'var(--ink3)', marginTop: 1 }}><Crayon /></span>
            </button>
          ) : (
            <button
              onClick={() => setEdite(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                height: 38, padding: '0 12px', borderRadius: 10,
                border: '1px dashed var(--line)', color: 'var(--ink3)',
                fontSize: 12.5, fontWeight: 600,
              }}
            >
              <Crayon /> Ajouter une note
            </button>
          )}
        </div>

        {surLeTerrain && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Bouton ton="principal" large taille={52} onClick={onDeplacer}>Déplacer</Bouton>
            <Bouton ton="neutre" large taille={52} onClick={onRemplacer}>Remplacer</Bouton>
            {/* Retirer est une action destructive : elle reste étroite et à l'écart des
                deux autres, parce qu'un faux contact dans le tiers bas d'un téléphone
                tenu d'une main dans une foule est attendu, pas accidentel. */}
            <Bouton ton="danger" taille={52} style={{ width: 52, padding: 0 }} aria-label="Retirer du terrain" onClick={onRetirer}>×</Bouton>
          </div>
        )}
      </div>
    </Feuille>
  );
}

function Tuile({ libelle, n, max = 9, icone, corrige, onTap }) {
  const plein = !corrige && n >= max;
  const inerte = corrige ? n === 0 : plein;
  const actif = n > 0;

  return (
    <button
      onClick={() => onTap(corrige ? -1 : 1)}
      disabled={inerte}
      aria-label={`${corrige ? 'Retirer' : 'Ajouter'} un ${libelle} — ${n} actuellement`}
      style={{
        height: 62, borderRadius: 14, padding: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
        background: corrige && actif ? 'color-mix(in oklch, var(--bad) 16%, transparent)' : actif ? 'var(--surf2)' : 'transparent',
        border: `1px solid ${corrige && actif ? 'var(--bad)' : actif ? 'var(--acc)' : 'var(--line)'}`,
        opacity: inerte ? 0.35 : 1,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--ink2)' }}>
        {icone}
        {/* Le compte ne s'affiche qu'à partir de un : un zéro devant chaque action
            donne quatre zéros à lire pour rien. */}
        {actif && <span style={{ fontFamily: MONO, fontWeight: 800, fontSize: 15, color: 'var(--ink)' }}>{n}</span>}
      </span>
      <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.3px', color: 'var(--ink2)' }}>{libelle}</span>
    </button>
  );
}

const Separateur = () => <div style={{ width: 1, background: 'var(--line)' }} />;

const Stat = ({ valeur, sur, libelle, accent }) => (
  <div style={{ flex: 1, padding: '12px 0 12px 12px' }}>
    <div style={{ fontFamily: MONO, fontWeight: 700, fontSize: 24, lineHeight: 1, letterSpacing: '-1px', color: accent ? 'var(--acc2)' : 'var(--ink)' }}>
      {valeur}{sur ? <span style={{ fontSize: 13, color: 'var(--ink3)' }}>/{sur}</span> : null}
    </div>
    <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '.7px', color: 'var(--ink2)', marginTop: 5 }}>{libelle}</div>
  </div>
);
